import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  AlertCircle,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  ExternalLink,
  Eye,
  Loader2,
  MoreHorizontal,
  Search,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { client } from "@/utils/orpc";
import { Badge } from "@super-resume-new/ui/components/badge";
import { Button } from "@super-resume-new/ui/components/button";
import { Card, CardContent } from "@super-resume-new/ui/components/card";
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "@super-resume-new/ui/components/dialog";
import { Input } from "@super-resume-new/ui/components/input";
import {
  Menu,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
} from "@super-resume-new/ui/components/menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@super-resume-new/ui/components/table";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/admin/jobs")({
  head: () =>
    seo({
      title: "Jobs Directory & Moderation | JobCues Admin",
      description: "Manage parsed job postings, re-parse with Cerebras LLM, review validation flags, and publish listings.",
      path: "/admin/jobs",
      noindex: true,
    }),
  component: AdminJobsPage,
});

function AdminJobsPage() {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "draft" | "expired">("all");
  const [page, setPage] = useState(1);
  const pageSize = 15;
  const [selectedJob, setSelectedJob] = useState<any>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  const {
    data: jobsData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["admin", "jobs", page, query, statusFilter],
    queryFn: () =>
      client.admin.listJobs({
        page,
        limit: pageSize,
        query: query.trim() || undefined,
        status: statusFilter,
      }),
  });

  // Status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: "active" | "draft" | "expired" }) => {
      return client.admin.updateJobStatus({ id, status });
    },
    onSuccess: (_, vars) => {
      toast.success(`Job marked as ${vars.status}`);
      refetch();
    },
    onError: (err: any) => toast.error(err.message || "Failed to update status"),
  });

  // Delete mutation
  const deleteJobMutation = useMutation({
    mutationFn: async (id: string) => {
      return client.admin.deleteJob({ id });
    },
    onSuccess: () => {
      toast.success("Job listing deleted");
      refetch();
    },
    onError: (err: any) => toast.error(err.message || "Failed to delete job"),
  });

  // LLM Reparse mutation
  const reparseMutation = useMutation({
    mutationFn: async (id: string) => {
      return client.admin.reparseJob({ id });
    },
    onSuccess: () => {
      toast.success("Job re-parsed with Cerebras LLM");
      refetch();
    },
    onError: (err: any) => toast.error(err.message || "Re-parse failed"),
  });

  const total = jobsData?.total ?? 0;
  const totalPages = jobsData?.totalPages ?? 1;

  return (
    <div className="space-y-4">
      {/* Search and Filters Bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="flex flex-1 items-center gap-2 w-full">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
            <Input
              placeholder="Search title, company, or location…"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
              className="pl-9 text-xs"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e: any) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="h-9 rounded-md border border-border bg-card px-2.5 text-xs text-foreground font-medium outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active (Published)</option>
            <option value="draft">Draft (Needs Review)</option>
            <option value="expired">Expired</option>
          </select>
        </div>

        <div className="text-xs text-muted-foreground tabular-nums">{total} jobs in database</div>
      </div>

      <Card className="border-border bg-card">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Posting Title &amp; Company</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Seniority / Mode</TableHead>
                <TableHead>Quality</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-12 text-center text-muted-foreground">
                    <Loader2 className="mx-auto size-6 animate-spin text-brand mb-2" />
                    Loading jobs catalog…
                  </TableCell>
                </TableRow>
              ) : jobsData?.jobs?.length ? (
                jobsData.jobs.map((j) => (
                  <TableRow key={j.id}>
                    <TableCell>
                      <div className="flex flex-col">
                        <Link
                          to="/jobs/$slug"
                          params={{ slug: j.slug }}
                          target="_blank"
                          className="font-semibold text-foreground hover:text-brand-foreground transition-colors flex items-center gap-1"
                        >
                          <span className="truncate max-w-[320px]">{j.title}</span>
                          <ExternalLink className="size-3 text-muted-foreground" />
                        </Link>
                        <span className="text-xs text-muted-foreground">
                          {j.company || "Unknown Company"} ·{" "}
                          {j.locationPrimary || j.country || "Remote"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[11px] uppercase">
                        {j.source}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      <span className="capitalize">{j.seniority || "mid"}</span>
                      {j.workMode && ` · ${j.workMode}`}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          j.descriptionQuality === "full"
                            ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/30"
                            : j.descriptionQuality === "partial"
                              ? "bg-amber-500/10 text-amber-500 border-amber-500/30"
                              : "bg-destructive/10 text-destructive border-destructive/30"
                        }
                      >
                        {j.descriptionQuality}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          j.status === "active"
                            ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/30 font-semibold"
                            : j.status === "draft"
                              ? "bg-amber-500/10 text-amber-500 border-amber-500/30 font-semibold"
                              : "bg-muted text-muted-foreground"
                        }
                      >
                        {j.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {j.status === "draft" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              updateStatusMutation.mutate({ id: j.id, status: "active" })
                            }
                            className="h-8 text-xs font-semibold text-emerald-500 gap-1"
                          >
                            <Check className="size-3" /> Publish
                          </Button>
                        )}

                        <Button
                          size="sm"
                          variant="ghost"
                          title="View Details & Validation"
                          onClick={async () => {
                            const full = await client.admin.getJob({ id: j.id });
                            setSelectedJob(full);
                            setDetailsDialogOpen(true);
                          }}
                          className="size-8 p-0"
                        >
                          <Eye className="size-3.5" />
                        </Button>

                        <Menu>
                          <MenuTrigger
                            render={
                              <button
                                type="button"
                                className="inline-flex size-8 items-center justify-center rounded-md border border-border bg-background p-1 text-muted-foreground hover:text-foreground cursor-pointer"
                              />
                            }
                          >
                            <MoreHorizontal className="size-3.5" />
                          </MenuTrigger>
                          <MenuPopup align="end" className="w-48">
                            <MenuItem onClick={() => reparseMutation.mutate(j.id)}>
                              <Sparkles className="size-3.5 text-muted-foreground" />
                              Re-parse with LLM
                            </MenuItem>

                            {j.status !== "active" && (
                              <MenuItem
                                onClick={() =>
                                  updateStatusMutation.mutate({ id: j.id, status: "active" })
                                }
                              >
                                <CheckCircle2 className="size-3.5 text-emerald-500" />
                                Mark as Active
                              </MenuItem>
                            )}

                            {j.status !== "draft" && (
                              <MenuItem
                                onClick={() =>
                                  updateStatusMutation.mutate({ id: j.id, status: "draft" })
                                }
                              >
                                <Clock className="size-3.5 text-amber-500" />
                                Move to Draft
                              </MenuItem>
                            )}

                            {j.status !== "expired" && (
                              <MenuItem
                                onClick={() =>
                                  updateStatusMutation.mutate({ id: j.id, status: "expired" })
                                }
                              >
                                <X className="size-3.5 text-muted-foreground" />
                                Mark as Expired
                              </MenuItem>
                            )}

                            <MenuSeparator />

                            <MenuItem
                              variant="destructive"
                              onClick={() => {
                                if (confirm(`Permanently delete ${j.title}?`)) {
                                  deleteJobMutation.mutate(j.id);
                                }
                              }}
                            >
                              <Trash2 className="size-3.5" />
                              Delete Listing
                            </MenuItem>
                          </MenuPopup>
                        </Menu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-muted-foreground text-sm">
                    No jobs match the current filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="gap-1"
          >
            <ChevronLeft className="size-3.5" /> Previous
          </Button>
          <span className="text-xs text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="gap-1"
          >
            Next <ChevronRight className="size-3.5" />
          </Button>
        </div>
      )}

      {/* Job Details & Validation Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogPopup className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between pr-6">
              <span>{selectedJob?.title}</span>
              <Badge variant="outline">{selectedJob?.status}</Badge>
            </DialogTitle>
            <DialogDescription>
              {selectedJob?.company} · {selectedJob?.source} ({selectedJob?.externalId})
            </DialogDescription>
          </DialogHeader>
          <DialogPanel className="space-y-4 py-2 text-xs">
            {/* Validation Flags */}
            {selectedJob?.validationProblemsParsed?.length > 0 ? (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-amber-500">
                <p className="font-semibold mb-1 flex items-center gap-1.5">
                  <AlertCircle className="size-4" /> Validation Warnings:
                </p>
                <ul className="list-disc pl-5 space-y-0.5">
                  {selectedJob.validationProblemsParsed.map((p: string, i: number) => (
                    <li key={i}>{p}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-2.5 text-emerald-500 flex items-center gap-1.5 font-medium">
                <CheckCircle2 className="size-4" /> Clean extraction · No validation problems
              </div>
            )}

            {/* Extracted Skills */}
            <div>
              <p className="font-semibold text-foreground mb-1.5">Extracted Skills:</p>
              <div className="flex flex-wrap gap-1.5">
                {selectedJob?.skills?.map((s: any) => (
                  <Badge
                    key={s.id}
                    variant="outline"
                    className={
                      s.kind === "must"
                        ? "bg-brand/10 text-brand-foreground border-brand/20"
                        : "bg-muted text-muted-foreground"
                    }
                  >
                    {s.name} ({s.kind})
                  </Badge>
                ))}
              </div>
            </div>

            {/* Intel JSON */}
            <div>
              <p className="font-semibold text-foreground mb-1.5">Structured Intel Payload:</p>
              <pre className="max-h-60 overflow-auto rounded-lg border border-border bg-muted/50 p-3 font-mono text-[11px] text-foreground">
                {JSON.stringify(selectedJob?.intelParsed, null, 2)}
              </pre>
            </div>
          </DialogPanel>
          <DialogFooter>
            <DialogClose render={<Button variant="outline">Close</Button>} />
          </DialogFooter>
        </DialogPopup>
      </Dialog>
    </div>
  );
}
